import { useMemo } from "react"

import {
  type Query as TCubeQuery,
  Filter,
  ResultSet,
} from "@cubejs-client/core"

import { CUBE_QUERY_KEYS_MAP, getCubeQueryBase } from "../../constants"
import useCampaignAdSetAdDetails, {
  prepareAllItemsDetails,
  useAllCampaignsAdSetsAdsDetails,
} from "~/ui-rtk/hooks/campaign-adset-ad-details"
import {
  getPivotData,
  polyfillCubeQueryWithTimeDimension,
} from "~/ui-rtk/utils/cube"

import {
  MetricsMapping,
  type PostProcessResultSync,
} from "~/ui-rtk/constants/metrics-mapping"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useYearDateRange } from "~/ui-rtk/hooks/date-range"

const defaultPrefixKey = "company_media_metrics_adsets"

const requests: Record<string, TCubeQuery> = {}
const results: Record<string, Record<string, unknown>> = {}

const ALL_CAMPAIGNS = "ALL"

export const useAllAdsetsCubeQuery = () => {
  const { postProcess } = MetricsMapping.brand_media_optimization_adsets_table
  const prefixKey = defaultPrefixKey
  const currentCompany = useCurrentCompany()
  const currentCompanyId = currentCompany?.id ?? "-company-not-set-"
  const campaignId = ALL_CAMPAIGNS
  const cacheKey = `${prefixKey}.${currentCompanyId}.${campaignId}`
  const queryAllCampaignKey = `${cacheKey}.all`
  const campaignIdKey = `${prefixKey}.campaign_id`
  const purchaseKey = `${prefixKey}.purchases`
  const dateRange = useYearDateRange()

  const allAdSetsQuery = useMemo(() => {
    if (!requests[queryAllCampaignKey]) {
      const base = getCubeQueryBase(prefixKey)
      requests[queryAllCampaignKey] = {
        ...base,
        measures: [...base.measures, purchaseKey],
        dimensions: [campaignIdKey],
      }
    }
    return polyfillCubeQueryWithTimeDimension(requests[queryAllCampaignKey], {
      dateRange,
    })
  }, [queryAllCampaignKey, prefixKey, purchaseKey, campaignIdKey, dateRange])

  const { [CUBE_QUERY_KEYS_MAP.ALL]: allAdSetsSet, isLoading } =
    useAllCampaignsAdSetsAdsDetails(allAdSetsQuery)

  if (!results[cacheKey]) {
    results[cacheKey] = {}
  }

  if (allAdSetsSet) {
    const pivotData = getPivotData(
      allAdSetsSet as ResultSet<Record<string, unknown>>,
      allAdSetsQuery,
    )
    const processedPivotData = postProcess
      ? (postProcess(pivotData, {}) as PostProcessResultSync)[0]
      : pivotData
    results[cacheKey].allAdSetsDetails =
      prepareAllItemsDetails(processedPivotData)
  }

  const { allAdSetsDetails } = results[cacheKey]

  return { allAdSetsDetails, isLoading }
}

export const useAdSetQubeQuery = (campaignId = ALL_CAMPAIGNS) => {
  const { postProcess } = MetricsMapping.brand_media_optimization_adsets_table
  const prefixKey = defaultPrefixKey
  const currentCompany = useCurrentCompany()
  const currentCompanyId = currentCompany?.id ?? "-company-not-set-"
  const cacheKey = `${prefixKey}.${currentCompanyId}.${campaignId}`
  const queryAllAdSetsKey = `${cacheKey}.all`
  const dateRange = useYearDateRange()

  const campaignIdKey = `${prefixKey}.campaign_id`
  const adSetIdKey = `${prefixKey}.adset_id`

  const allAdSetsFilters = useMemo(() => {
    const base = getCubeQueryBase(prefixKey)
    const filters = {
      and: [...(base.filters ?? [])],
    }
    if (campaignId !== ALL_CAMPAIGNS) {
      filters.and.push({
        dimension: campaignIdKey,
        operator: "equals",
        values: [campaignId],
      })
    }
    return filters
  }, [prefixKey, campaignId, campaignIdKey])

  const allAdSetsQuery = useMemo(() => {
    if (!requests[queryAllAdSetsKey]) {
      const base = getCubeQueryBase(prefixKey)
      requests[queryAllAdSetsKey] = {
        ...base,
        dimensions: [adSetIdKey],
        filters: [allAdSetsFilters as Filter],
      }
    }
    return polyfillCubeQueryWithTimeDimension(requests[queryAllAdSetsKey], {
      dateRange,
    })
  }, [queryAllAdSetsKey, prefixKey, adSetIdKey, allAdSetsFilters, dateRange])

  const { [CUBE_QUERY_KEYS_MAP.ALL]: allAdSetsSet, isLoading } =
    useCampaignAdSetAdDetails({
      [CUBE_QUERY_KEYS_MAP.ALL]: allAdSetsQuery,
    })

  if (!results[cacheKey]) {
    results[cacheKey] = {}
  }

  if (allAdSetsSet) {
    const pivotData = getPivotData(
      allAdSetsSet as ResultSet<Record<string, unknown>>,
      allAdSetsQuery,
    )
    const processedPivotData = postProcess
      ? (postProcess(pivotData, {}) as PostProcessResultSync)[0]
      : pivotData
    results[cacheKey].allAdSetsDetails =
      prepareAllItemsDetails(processedPivotData)
  }

  const { allAdSetsDetails } = results[cacheKey]

  return { allAdSetsDetails, isLoading }
}
